import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  generatePath,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import dayjs from 'dayjs';
import axios from 'axios';

import Button from '../components/buttons/Button';
import FieldCard from '../components/cards/FieldCard';
import Breadcrumb from '../components/ui/Breadcrumb';
import ArenaCalendar from '../components/calendar/arena-calendar/ArenaCalendar';
import ArenaServiceIcon from '../components/ui/ArenaServiceIcon';
import LoadingBall from '../components/loading/LoadingBall';
import { getToken } from '../utils/token';
import { Field, Game } from '../types';
import { useAuth } from '../hooks/useAuth';
import { useComplexes } from '../hooks/useComplexes';
import { COMMON_TRANSLATE_KEYS } from '../constants/translate-keys';
import { getFieldGamesWithReservationsByDatePeriod } from '../api/reservations';
import { useNotifications } from '../hooks/notifications/useNotifications';
import { ROUTE_FIELD_STATISTICS } from '../router/constants';

const ArenaFieldReservation: React.FC = () => {
  const { id, fieldId } = useParams<{ id: string; fieldId: string }>();
  const [query] = useSearchParams();

  const { t } = useTranslation();

  const [games, setGames] = useState<Game[]>([]);

  const [field, setField] = useState<Field | null>(null);

  const navigate = useNavigate();

  const { user } = useAuth();

  const { latestNotification } = useNotifications();

  const { complexes } = useComplexes();

  const complex = useMemo(() => {
    return complexes.find((c) => c.id === Number(id)) || null;
  }, [complexes, id]);

  const dateInQuery = useMemo(() => {
    const date = query.get('date');
    return date && dayjs(date).isValid() ? new Date(date) : null;
  }, [query]);

  const reservationsCount = useMemo(() => {
    return games.filter((r) => ['CONFIRMED', 'PENDING'].includes(r.status))
      .length;
  }, [games]);

  const unavailableFieldTooltip = useMemo(() => {
    if (field?.isUnavailable) {
      return t(COMMON_TRANSLATE_KEYS.FIELD_IS_UNAVAILABLE);
    }

    return '';
  }, [field, t]);

  const fetchField = useCallback(async () => {
    try {
      const response = await axios.get<Field>(
        `${process.env.REACT_APP_API_ENDPOINT}/owner/complexes/${id}/fields/${fieldId}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Accept-Language': 'en',
          },
          params: {
            excludeExpired: true,
          },
        }
      );

      setField(response.data);
    } catch (error) {
      console.error('Error fetching field with reservations:', error);
    }
  }, [id, fieldId]);

  const fetchReservations = useCallback(
    async (startDate: Date, endDate: Date) => {
      const { data, error } = await getFieldGamesWithReservationsByDatePeriod(
        Number(id),
        Number(fieldId),
        startDate,
        endDate
      );

      if (error) {
        toast.error(error.error);
        return;
      }

      if (data) {
        setGames(data);
      }
    },
    [id, fieldId, setGames]
  );

  useEffect(() => {
    fetchField();
  }, [fetchField]);

  if (!field || !complex) {
    return <LoadingBall className="w-full h-screen" />;
  }

  return (
    <div className="text-mainWhite mx-[30px]">
      <Breadcrumb
        className="sm:mb-8 mb-10"
        links={[
          {
            name: t(COMMON_TRANSLATE_KEYS.COMPLEXES),
            to: '/my-fields',
          },
          {
            name: `${field.complexName} (${t(COMMON_TRANSLATE_KEYS.COMPLEX)})`,
            to: `/arena/${field.complexId}/info`,
          },
        ]}
      >
        {field.name} ({t(COMMON_TRANSLATE_KEYS.FIELD)})
      </Breadcrumb>
      <FieldCard
        field={field}
        title={field.name}
        location={`${field.city}, ${field.country} | ${field.address}`}
        width="xl"
        complex={complex}
      >
        <div className="flex lg:flex-row lg:flex-nowrap flex-wrap flex-col justify-end gap-2 lg:items-end items-center lg:w-[55%]">
          {/* TODO: Add field service icons here. Could extract to separate component. Add tooltip */}
          <div className="flex flex-nowrap flex-shrink-0 gap-2 justify-center">
            {Object.keys(field)
              .filter((k) => !!field[k as keyof Field])
              .map((k) => (
                <ArenaServiceIcon
                  key={k}
                  service={k as keyof Field}
                  width={24}
                  height={24}
                />
              ))}
          </div>

          {/* <div className="flex md:flex-row flex-col gap-4 items-center flex-wrap md:justify-end"> */}
          <Button
            backgroundColor="bg-functionalGreen"
            width={12}
            textColor="text-gray-800"
            className="flex-nowrap whitespace-nowrap"
            onClick={() =>
              navigate(`/book-field/${field.complexId}/${field.id}`)
            }
            isDisabled={field.isUnavailable}
            data-tooltip-id="tooltip"
            data-tooltip-content={unavailableFieldTooltip}
          >
            {t(COMMON_TRANSLATE_KEYS.BOOK_FIELD)}
          </Button>
          <Button
            border="border-mainBlue"
            backgroundColor="bg-gray-800"
            width={12}
            textColor="text-mainBlue"
            onClick={() => navigate(`/edit-field-info/${id}/${fieldId}`)}
          >
            {t(COMMON_TRANSLATE_KEYS.EDIT_INFO)}
          </Button>
          <Button
            border="border-mainBlue"
            backgroundColor="bg-gray-800"
            width={12}
            textColor="text-mainBlue"
            className="flex-nowrap whitespace-nowrap"
            onClick={() =>
              navigate(
                generatePath(ROUTE_FIELD_STATISTICS, {
                  id: String(field.complexId),
                  fieldId: String(field.id),
                })
              )
            }
          >
            {t(COMMON_TRANSLATE_KEYS.SEE_STATISTICS)}
          </Button>
        </div>
      </FieldCard>
      <div className="flex md:flex-row flex-col gap-8 mt-6 h-full">
        <div className="w-full h-full">
          <div className="flex flex-row justify-between mb-4">
            <p>{` ${t(
              COMMON_TRANSLATE_KEYS.RESERVATIONS
            )} (${reservationsCount})`}</p>
          </div>
          {user?.cancelReservationHours && (
            <p className="text-center w-full border border-settingsDividerGray p-5 rounded-lg">
              {t(COMMON_TRANSLATE_KEYS.CANCEL_RESERVATION_NOTICE, {
                hours: user.cancelReservationHours,
                count: user.cancelReservationHours,
              })}
            </p>
          )}
          <div className="flex flex-col col-span-1 justify-between gap-y-4 pb-10 h-full">
            <ArenaCalendar
              id={id}
              field={field}
              games={games}
              setGames={setGames}
              initialStartDate={dateInQuery}
              fetchGames={fetchReservations}
              complex={complex}
              newReservation={latestNotification?.reservation}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArenaFieldReservation;
